import {DOCUMENT} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  ViewChild,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {ASSET_MAP, AssetMap} from '@core/constants/client-asset-maps';
import {AuthService} from '@core/services/auth.service';
import {Language, LanguageService} from '@core/services/language.service';
import {LOCAL_STORAGE, WINDOW} from '@ng-web-apis/common';
import {Store} from '@ngxs/store';
import {AuthState} from '@store/auth/auth.state';
import {LoadBlockedCountires} from '@store/blocked-countries/blocked-countries.actions';
import {filter, map, take} from 'rxjs';
import {TrackingService} from '@core/services/tracking.service';
import {ConsentBannerComponent} from '@ui/consent-banner/consent-banner.component';
import {PREFFERED_LANGUAGE} from '@core/constants/localstorage-keys';
import {CurrentClientState} from '@store/current-client/current-client.state';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent {
  @ViewChild(ConsentBannerComponent) readonly consentBannerComponentRef:
    | ConsentBannerComponent
    | undefined;

  readonly isCMPEnabled$ = this.store.select(CurrentClientState.isCMPEnabled);

  readonly isGdprClient = this.trackingService.isGdprClient;

  readonly COOKIES_ACCEPTED = 'cookiesAccepted';

  isComingSoon = false;

  readonly isPrerenderer = this.window.navigator.userAgent
    .toLowerCase()
    .includes('prerender');

  get cookiesAccepted(): boolean {
    return this.localStorage.getItem(this.COOKIES_ACCEPTED) === String(true);
  }

  hideWhenWebViewQueryParam$ = this.activatedRoute.queryParams.pipe(
    map(({webview}) => webview),
  );

  constructor(
    readonly activatedRoute: ActivatedRoute,
    readonly router: Router,
    readonly authService: AuthService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    @Inject(LOCAL_STORAGE) readonly localStorage: Storage,
    readonly store: Store,
    @Inject(WINDOW) readonly window: Window & {bradmax: any},
    @Inject(DOCUMENT) readonly document: Document,
    @Inject(ASSET_MAP) readonly assetMap: AssetMap,
    readonly trackingService: TrackingService,
    readonly languageService: LanguageService,
  ) {
    this.store
      .select(AuthState.token)
      .pipe(
        filter(token => token !== undefined),
        take(1),
      )
      .subscribe(() => this.store.dispatch(LoadBlockedCountires));
    authService.listenForLocalStorageTokenChanges();

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.window.document.body.scrollTo(0, 0));

    if (assetMap) {
      // Parse asset map to css custom properties so the assets can be used in stylesheets
      const _parseAssetMapToCssVariables = (obj: any, currentKey: string) => {
        const root = this.document.querySelector<HTMLElement>(':root');

        Object.keys(obj).forEach(key => {
          const nextKey = currentKey ? `${currentKey}-${key}` : key;

          if (typeof obj[key] === 'object' && obj[key] !== null) {
            _parseAssetMapToCssVariables(obj[key], nextKey);
          } else if (typeof obj[key] === 'string') {
            root?.style.setProperty(`--assets-${nextKey}`, `url(${obj[key]})`);
          }
        });
      };

      _parseAssetMapToCssVariables(assetMap, '');
    }

    this.document.addEventListener('click', () => {
      this.window.bradmax?.player?.collectClickForLaterAutoplay();
    });
  }

  acceptCookies(): void {
    this.localStorage.setItem(this.COOKIES_ACCEPTED, String(true));
    this._changeDetectorRef.detectChanges();
  }

  changeLanguage(newLanguage: Language) {
    const oldLanguage = this.activatedRoute.firstChild?.snapshot.paramMap.get('language');

    if (oldLanguage && oldLanguage !== newLanguage) {
      this.languageService.setLanguage(newLanguage);
      this.localStorage.setItem(PREFFERED_LANGUAGE, newLanguage);

      this.window.location = this.window.location.pathname.replace(
        `/${oldLanguage}/`,
        `/${newLanguage}/`,
      );
    }
  }

  onCookieSettingsClicked() {
    this.consentBannerComponentRef?.showBannerWithDetailedLayer();
  }
}
